/**
 * SendToAllDialog — Owner spec 2026-02-08.
 *
 * "שלח לכולם" popup for a production. Talks to the external FlowUpSender
 * gateway through our /api/sender/* proxy. Flow:
 *   1) Fetch devices → user picks one (or hits "חבר חדש" → QR / pair code).
 *   2) User uploads a file + writes text (optional).
 *   3) Full recipient list (crew_roster + additional_contacts) shows with
 *      checkboxes. Deselect anyone you don't want.
 *   4) Send → per-recipient status returns.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import {
  Loader2, Send, RefreshCw, QrCode, Smartphone, Upload as UploadIcon,
  CheckCircle2, XCircle, MinusCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

export function SendToAllDialog({ open, onOpenChange, production }) {
  // ------- Devices -------
  const [devices, setDevices]       = useState([]);
  const [deviceId, setDeviceId]     = useState(null);
  const [devicesBusy, setDevicesBusy] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);

  // ------- Message body -------
  const [content, setContent]       = useState("");
  const [file, setFile]             = useState(null);
  const [uploading, setUploading]   = useState(false);
  const [templateId, setTemplateId] = useState(null);
  const [objectPath, setObjectPath] = useState("");

  // Recipient list — owner spec 2026-02-08b — each entry carries the
  // crew member's own `days` + `hours` so the backend can render
  // {ימים_ושעות} tokens personalized per-recipient.
  const recipients = useMemo(() => {
    const out = [];
    const buildHoursLines = (r) => {
      const days = r?.days || [];
      const hours = r?.hours || {};
      const lines = [];
      for (let i = 0; i < days.length; i++) {
        if (!days[i]) continue;
        const h = hours[String(i)];
        const t = h && (h.start || h.end)
          ? `${h.start || "--:--"}${h.end ? `–${h.end}` : ""}`
          : "";
        lines.push(t ? `יום ${i + 1}: ${t}` : `יום ${i + 1}`);
      }
      return lines;
    };
    for (const r of (production?.crew_roster || [])) {
      if (!r?.phone) continue;
      const name = r.name || r.role || "צוות";
      out.push({
        id: `crew:${r.id}`,
        name,
        firstName: (name.split(" ")[0] || name),
        phone: r.phone,
        source: "crew",
        hours_lines: buildHoursLines(r),
      });
    }
    for (const c of (production?.additional_contacts || [])) {
      if (!c?.phone) continue;
      const name = c.name || c.role || "איש קשר";
      out.push({
        id: `contact:${c.id}`,
        name,
        firstName: (name.split(" ")[0] || name),
        phone: c.phone,
        source: "contact",
        hours_lines: [],
      });
    }
    return out;
  }, [production?.crew_roster, production?.additional_contacts]);

  const [selected, setSelected] = useState(new Set());
  const [sending, setSending]   = useState(false);
  const [results, setResults]   = useState(null); // {sent, total, results}
  const textareaRef = useRef(null);

  // Owner spec 2026-02-08c — send scheduling. `sendMode` = 'now' | 'later'.
  const [sendMode, setSendMode] = useState("now");
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [scheduleOpen, setScheduleOpen] = useState(false);

  // Owner spec 2026-02-08b — insert a token at cursor position. Falls
  // back to appending if we don't have a valid cursor (e.g., textarea
  // never focused).
  const insertToken = (token) => {
    const ta = textareaRef.current;
    if (!ta) { setContent((c) => c + token); return; }
    const start = ta.selectionStart ?? content.length;
    const end   = ta.selectionEnd   ?? content.length;
    const next = content.slice(0, start) + token + content.slice(end);
    setContent(next);
    // Restore focus + place cursor after the inserted token
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + token.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  // Seed selection = ALL on open
  useEffect(() => {
    if (!open) return;
    setSelected(new Set(recipients.map((r) => r.id)));
    setResults(null);
    setContent("");
    setFile(null);
    setTemplateId(null);
    setObjectPath("");
    // Load devices
    (async () => {
      setDevicesBusy(true);
      try {
        const { data } = await api.get("/sender/devices");
        setDevices(Array.isArray(data) ? data : []);
        // Auto-pick first connected device
        const connected = (Array.isArray(data) ? data : []).find((d) => d.status === "connected" || d.connected);
        if (connected) setDeviceId(connected.id);
      } catch {
        toast.error("לא הצלחתי לטעון מכשירים מ-FlowUpSender");
      } finally {
        setDevicesBusy(false);
      }
    })();
     
  }, [open, production?.id]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(recipients.map((r) => r.id)));
  const selectNone = () => setSelected(new Set());

  const uploadFile = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: up } = await api.post("/sender/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setObjectPath(up.objectPath);
      // Detect mediaType from mime
      const mime = (f.type || "").toLowerCase();
      const mediaType = mime.startsWith("image/") ? "image"
        : mime.startsWith("video/") ? "video"
        : "document";
      const { data: tpl } = await api.post("/sender/templates", {
        name: `FlowUp ${new Date().toLocaleString("he-IL")}`,
        content: content || "",
        mediaType,
        mediaPath: up.objectPath,
      });
      setTemplateId(tpl.id);
      toast.success("הקובץ הועלה בהצלחה");
    } catch (err) {
      toast.error("העלאת קובץ נכשלה");
      setFile(null);
    } finally {
      setUploading(false);
    }
  };

  const onFilePick = (f) => {
    setFile(f);
    setTemplateId(null);
    if (f) uploadFile(f);
  };

  // Owner spec 2026-02-08c — instead of firing send immediately, we
  // open the schedule dialog so the SA picks 'now' or 'later'.
  const openScheduleDialog = () => {
    if (!deviceId) { toast.error("בחר מכשיר מחובר"); return; }
    if (selected.size === 0) { toast.error("בחר לפחות איש קשר אחד"); return; }
    if (!content && !templateId) { toast.error("כתוב טקסט או צרף קובץ"); return; }
    setSendMode("now");
    setScheduleDate("");
    setScheduleTime("");
    setScheduleOpen(true);
  };

  const sendAll = async ({ scheduledFor } = {}) => {
    setSending(true);
    setResults(null);
    try {
      const list = recipients.filter((r) => selected.has(r.id))
        .map(({ name, phone, firstName, hours_lines }) => ({ name, phone, firstName, hours_lines }));
      const payload = { deviceId, content, templateId, recipients: list, delaySec: 3 };
      if (scheduledFor) payload.scheduledFor = scheduledFor;
      const { data } = await api.post("/sender/broadcast", payload);
      setResults(data);
      if (data.scheduled) {
        toast.success(`ההודעה תוזמנה ל-${new Date(data.scheduledFor).toLocaleString("he-IL")}`);
      } else {
        toast.success(`נשלחו ${data.sent}/${data.total} הודעות`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail?.slice?.(0, 200) || "שליחה נכשלה");
    } finally {
      setSending(false);
    }
  };

  const confirmSchedule = async () => {
    let scheduledFor = null;
    if (sendMode === "later") {
      if (!scheduleDate || !scheduleTime) { toast.error("בחר תאריך ושעה"); return; }
      // Local date/time → ISO string
      const iso = new Date(`${scheduleDate}T${scheduleTime}`).toISOString();
      if (new Date(iso).getTime() <= Date.now() + 10000) {
        toast.error("הזמן חייב להיות בעתיד");
        return;
      }
      scheduledFor = iso;
    }
    setScheduleOpen(false);
    await sendAll({ scheduledFor });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          dir="rtl"
          className="bg-stone-900 border-white/10 text-stone-100 sm:max-w-2xl max-h-[92vh] overflow-y-auto"
          data-testid="ff-send-all-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-right flex items-center gap-2">
              <Send className="h-5 w-5 text-emerald-400" />
              <span className="bg-gradient-to-l from-emerald-300 to-teal-300 bg-clip-text text-transparent">
                שלח הודעה לכולם
              </span>
            </DialogTitle>
            <DialogDescription className="text-right text-stone-400 text-xs">
              שליחה מרוכזת דרך FlowUpSender לכל אנשי הצוות ואנשי הקשר של ההפקה.
            </DialogDescription>
          </DialogHeader>

          {/* 1) Device picker */}
          <div className="rounded-lg border border-white/10 bg-black/20 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-bold">
                <Smartphone className="h-4 w-4 text-sky-400" />
                טלפון שולח
              </div>
              <div className="flex items-center gap-1.5">
                <Button
                  onClick={() => setConnectOpen(true)}
                  variant="outline"
                  className="bg-transparent border-white/20 text-stone-200 hover:bg-white/10 h-7 text-xs"
                  data-testid="ff-send-all-connect-new"
                >
                  <QrCode className="h-3.5 w-3.5 ml-1" /> חבר חדש
                </Button>
                <Button
                  onClick={async () => {
                    setDevicesBusy(true);
                    try {
                      const { data } = await api.get("/sender/devices");
                      setDevices(Array.isArray(data) ? data : []);
                    } finally { setDevicesBusy(false); }
                  }}
                  variant="outline"
                  className="bg-transparent border-white/20 text-stone-200 hover:bg-white/10 h-7 w-7 p-0"
                  data-testid="ff-send-all-refresh-devices"
                  title="רענן"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${devicesBusy ? "animate-spin" : ""}`} />
                </Button>
              </div>
            </div>
            {devicesBusy && devices.length === 0 ? (
              <div className="text-stone-500 text-xs flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> טוען מכשירים...
              </div>
            ) : devices.length === 0 ? (
              <div className="text-amber-300 text-xs">אין מכשירים מחוברים. לחץ &quot;חבר חדש&quot;.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {devices.map((d) => {
                  const isConnected = d.status === "connected" || d.connected;
                  const isPicked = deviceId === d.id;
                  return (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => setDeviceId(d.id)}
                      disabled={!isConnected}
                      className={`text-right rounded-md border px-2.5 py-1.5 text-xs transition ${
                        isPicked
                          ? "bg-emerald-500 border-emerald-300 text-black font-extrabold"
                          : isConnected
                          ? "bg-white/5 border-white/15 hover:bg-emerald-500/20 hover:border-emerald-400/60"
                          : "bg-black/40 border-white/10 text-stone-500 cursor-not-allowed"
                      }`}
                      data-testid={`ff-send-all-device-${d.id}`}
                    >
                      <div className="font-bold truncate">{d.name || `Device ${d.id}`}</div>
                      <div className={`text-[10px] ${isPicked ? "text-stone-800" : isConnected ? "text-emerald-300" : "text-red-400"}`}>
                        {isConnected ? "מחובר" : (d.status || "מנותק")}
                        {d.phoneNumber && (<span className="mr-1.5" dir="ltr">· {d.phoneNumber}</span>)}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* 2) Message body */}
          <div className="rounded-lg border border-white/10 bg-black/20 p-3 space-y-2">
            <div className="text-sm font-bold flex items-center gap-2">
              <UploadIcon className="h-4 w-4 text-amber-400" />
              קובץ + מלל
            </div>
            {/* Owner spec 2026-02-08b — quick insert tokens. Each token
                is expanded per-recipient by the backend, so the same
                template produces personalized messages. */}
            <div className="flex items-center gap-1.5 flex-wrap">
              <button
                type="button"
                onClick={() => insertToken("{שם}")}
                className="bg-sky-500/30 hover:bg-sky-500/50 border border-sky-400/40 text-sky-100 text-xs font-bold rounded px-2 py-1 transition"
                data-testid="ff-send-all-token-name"
                title="הודעה תיפתח בשם הפרטי של כל נמען"
              >
                + שם הפרטי
              </button>
              <button
                type="button"
                onClick={() => insertToken("{ימים_ושעות}")}
                className="bg-emerald-500/30 hover:bg-emerald-500/50 border border-emerald-400/40 text-emerald-100 text-xs font-bold rounded px-2 py-1 transition"
                data-testid="ff-send-all-token-schedule"
                title="ההודעה תכיל את ימי הצילום והשעות של כל איש צוות"
              >
                + ימים ושעות
              </button>
              <span className="text-stone-500 text-[10px] mr-1">כל נמען יקבל את הפרטים שלו</span>
            </div>
            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={5}
              placeholder={"היי {שם},\nהנה סידור העבודה שלך:\n{ימים_ושעות}"}
              className="w-full bg-stone-800 border border-white/15 rounded-md px-3 py-2 text-sm text-stone-100 outline-none focus:border-emerald-400/60 resize-y leading-relaxed"
              data-testid="ff-send-all-content"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <label
                className="cursor-pointer bg-white/5 border border-white/15 rounded-md px-3 py-1.5 text-xs hover:bg-white/10 inline-flex items-center gap-1.5"
                data-testid="ff-send-all-file-picker"
              >
                <UploadIcon className="h-3.5 w-3.5" />
                {file ? file.name : "בחר קובץ (אופציונלי)"}
                <input
                  type="file"
                  className="hidden"
                  onChange={(e) => onFilePick(e.target.files?.[0] || null)}
                  accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                />
              </label>
              {uploading && (
                <span className="text-xs text-stone-400 inline-flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> מעלה...
                </span>
              )}
              {templateId && (
                <span className="text-xs text-emerald-300 font-bold" data-testid="ff-send-all-file-ready">
                  ✓ הקובץ מוכן לשליחה
                </span>
              )}
              {file && !uploading && (
                <button
                  type="button"
                  onClick={() => { setFile(null); setTemplateId(null); setObjectPath(""); }}
                  className="text-xs text-red-400 hover:text-red-300"
                  data-testid="ff-send-all-file-clear"
                >
                  הסר
                </button>
              )}
            </div>
          </div>

          {/* 3) Recipients */}
          <div className="rounded-lg border border-white/10 bg-black/20 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-bold">
                נמענים ({selected.size}/{recipients.length})
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={selectAll}
                  className="text-emerald-300 hover:text-emerald-200 text-xs font-bold underline underline-offset-2"
                  data-testid="ff-send-all-select-all"
                >
                  בחר הכל
                </button>
                <span className="text-stone-600">·</span>
                <button
                  type="button"
                  onClick={selectNone}
                  className="text-stone-400 hover:text-stone-200 text-xs font-bold underline underline-offset-2"
                  data-testid="ff-send-all-select-none"
                >
                  נקה
                </button>
              </div>
            </div>
            {recipients.length === 0 ? (
              <div className="text-amber-300 text-xs">אין אנשי קשר עם טלפון בהפקה הזו.</div>
            ) : (
              <div className="max-h-64 overflow-y-auto space-y-1" data-testid="ff-send-all-recipients">
                {recipients.map((r) => {
                  const picked = selected.has(r.id);
                  const result = results?.results?.find((x) => x.phone.replace(/\D/g, "").endsWith(r.phone.replace(/\D/g, "").slice(-9)));
                  return (
                    <label
                      key={r.id}
                      className={`flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-xs cursor-pointer transition ${
                        picked ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-white/5 border border-white/10 hover:bg-white/10"
                      }`}
                      data-testid={`ff-send-all-recipient-${r.id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <input
                          type="checkbox"
                          checked={picked}
                          onChange={() => toggle(r.id)}
                          className="accent-emerald-500 h-4 w-4"
                          data-testid={`ff-send-all-check-${r.id}`}
                        />
                        <div className="min-w-0">
                          <div className="text-white font-bold truncate">{r.name}</div>
                          <div className="text-stone-400 text-[10px]" dir="ltr">{r.phone}</div>
                        </div>
                      </div>
                      {result && (
                        <div className="shrink-0">
                          {result.status === "sent" || result.status === "queued" ? (
                            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                          ) : result.status === "skipped" ? (
                            <MinusCircle className="h-4 w-4 text-stone-500" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-400" />
                          )}
                        </div>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {results && !results.scheduled && (
            <div className="text-xs text-stone-300 border border-white/10 rounded-md p-2 bg-black/20" data-testid="ff-send-all-summary">
              נשלחו בהצלחה: <span className="text-emerald-300 font-bold">{results.sent}</span> / {results.total}
            </div>
          )}
          {results?.scheduled && (
            <div className="text-xs text-sky-200 border border-sky-500/30 rounded-md p-2 bg-sky-500/10" data-testid="ff-send-all-scheduled-summary">
              ✓ ההודעה תוזמנה ל-{new Date(results.scheduledFor).toLocaleString("he-IL")}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10 mt-1">
            <Button
              onClick={() => onOpenChange(false)}
              variant="outline"
              disabled={sending}
              className="bg-transparent border-white/20 text-stone-200 hover:bg-white/10"
              data-testid="ff-send-all-close"
            >
              סגור
            </Button>
            <Button
              onClick={openScheduleDialog}
              disabled={sending || !deviceId || selected.size === 0 || uploading}
              className="bg-emerald-500 hover:bg-emerald-400 text-black font-extrabold"
              data-testid="ff-send-all-send"
            >
              {sending ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" /> שולח...</>) : (<><Send className="h-4 w-4 mr-1" /> שלח לכולם ({selected.size})</>)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConnectDeviceDialog
        open={connectOpen}
        onOpenChange={setConnectOpen}
        onConnected={(newDevice) => {
          setDevices((prev) => [...prev.filter((d) => d.id !== newDevice.id), newDevice]);
          setDeviceId(newDevice.id);
          setConnectOpen(false);
          toast.success("המכשיר חובר");
        }}
      />

      {/* Owner spec 2026-02-08c — schedule confirmation dialog. */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent dir="rtl" className="bg-stone-900 border-white/10 text-stone-100 sm:max-w-md" data-testid="ff-send-all-schedule-dialog">
          <DialogHeader>
            <DialogTitle className="text-right flex items-center gap-2">
              <Send className="h-5 w-5 text-emerald-400" />
              מתי לשלוח?
            </DialogTitle>
            <DialogDescription className="text-right text-stone-400 text-xs">
              בחר עכשיו לשליחה מיידית, או &quot;מתוזמן&quot; כדי לתזמן ליום ושעה.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setSendMode("now")}
                className={`rounded-lg border-2 px-3 py-3 text-center transition ${
                  sendMode === "now"
                    ? "bg-emerald-500 border-emerald-300 text-black font-extrabold"
                    : "bg-white/5 border-white/15 hover:bg-emerald-500/20"
                }`}
                data-testid="ff-send-all-schedule-now"
              >
                <div className="text-sm font-bold">מיד</div>
                <div className="text-[10px] opacity-80">שלח עכשיו</div>
              </button>
              <button
                type="button"
                onClick={() => setSendMode("later")}
                className={`rounded-lg border-2 px-3 py-3 text-center transition ${
                  sendMode === "later"
                    ? "bg-sky-500 border-sky-300 text-black font-extrabold"
                    : "bg-white/5 border-white/15 hover:bg-sky-500/20"
                }`}
                data-testid="ff-send-all-schedule-later"
              >
                <div className="text-sm font-bold">מתוזמן</div>
                <div className="text-[10px] opacity-80">שלח בזמן שנבחר</div>
              </button>
            </div>
            {sendMode === "later" && (
              <div className="grid grid-cols-2 gap-2 pt-1">
                <div>
                  <label className="block text-xs font-bold text-stone-300 mb-1">תאריך</label>
                  <input
                    type="date"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    className="w-full bg-stone-800 border border-white/15 rounded-md px-3 py-2 text-sm text-stone-100 outline-none focus:border-sky-400/60"
                    data-testid="ff-send-all-schedule-date"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-300 mb-1">שעה</label>
                  <input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    className="w-full bg-stone-800 border border-white/15 rounded-md px-3 py-2 text-sm text-stone-100 outline-none focus:border-sky-400/60"
                    data-testid="ff-send-all-schedule-time"
                  />
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10 mt-2">
            <Button
              onClick={() => setScheduleOpen(false)}
              variant="outline"
              className="bg-transparent border-white/20 text-stone-200 hover:bg-white/10"
              data-testid="ff-send-all-schedule-cancel"
            >
              ביטול
            </Button>
            <Button
              onClick={confirmSchedule}
              disabled={sending || (sendMode === "later" && (!scheduleDate || !scheduleTime))}
              className="bg-emerald-500 hover:bg-emerald-400 text-black font-extrabold"
              data-testid="ff-send-all-schedule-confirm"
            >
              {sending ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" /> שולח...</>) : sendMode === "now" ? "שלח עכשיו" : "תזמן שליחה"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ------------------------------------------------------------------
// ConnectDeviceDialog — QR/pair popup. Creates a device, polls its
// status until connected, then hands the record back.
// ------------------------------------------------------------------
function ConnectDeviceDialog({ open, onOpenChange, onConnected }) {
  const [mode, setMode]         = useState("qr"); // 'qr' | 'pair'
  const [phone, setPhone]       = useState("");
  const [device, setDevice]     = useState(null);
  const [qr, setQr]             = useState("");
  const [pairingCode, setPairingCode] = useState("");
  const [status, setStatus]     = useState("idle"); // idle|starting|waiting|connected|error
  const [msg, setMsg]           = useState("");

  useEffect(() => {
    if (!open) {
      setDevice(null); setQr(""); setPairingCode(""); setStatus("idle"); setMsg(""); setPhone("");
      return;
    }
     
  }, [open]);

  const startFlow = async () => {
    if (!phone.trim()) { toast.error("הזן מספר טלפון"); return; }
    setStatus("starting");
    setMsg("");
    try {
      const digits = phone.replace(/\D/g, "").replace(/^0/, "972");
      const { data: dev } = await api.post("/sender/devices", {
        name: `FlowUp ${digits}`,
        phoneNumber: digits,
        provider: "baileys",
      });
      setDevice(dev);
      // Owner spec 2026-02-08d — check if device is already connected
      // (baileys sometimes restores an existing session and never issues
      // a QR). If so, short-circuit to the connected state.
      try {
        const { data: st } = await api.get(`/sender/devices/${dev.id}/status`);
        if (st?.connected || st?.status === "connected") {
          setStatus("connected");
          onConnected({ ...dev, status: "connected", connected: true });
          return;
        }
      } catch { /* fall through */ }
      if (mode === "pair") {
        const { data } = await api.post(`/sender/devices/${dev.id}/pair`, { phoneNumber: digits });
        setPairingCode(data.pairingCode || "");
        setStatus("waiting");
      } else {
        const { data } = await api.post(`/sender/devices/${dev.id}/session`, {});
        setQr(data.qrcode || "");
        setStatus("waiting");
      }
    } catch (err) {
      setStatus("error");
      setMsg(err?.response?.data?.detail?.slice?.(0, 200) || "שגיאה בהתחלת חיבור");
    }
  };

  // Poll status when waiting — plus watchdog: if no QR arrives within
  // 30 seconds AND we're in QR mode, show a retry button so the user
  // isn't stuck staring at a spinner.
  useEffect(() => {
    if (status !== "waiting" || !device?.id) return;
    let cancelled = false;
    let elapsed = 0;
    const tick = async () => {
      elapsed += 2.5;
      try {
        const { data } = await api.get(`/sender/devices/${device.id}/status`);
        if (cancelled) return;
        if (data.connected || data.status === "connected") {
          setStatus("connected");
          onConnected({ ...device, status: "connected", connected: true });
          return;
        }
        if (mode === "qr") {
          try {
            const { data: qd } = await api.get(`/sender/devices/${device.id}/qr`);
            if (!cancelled && qd?.qrcode) setQr(qd.qrcode);
          } catch (e) {
            console.warn("qr poll failed", e);
          }
        }
        if (mode === "qr" && elapsed > 30 && !qr) {
          setStatus("error");
          setMsg("ה-QR לא הגיע. ייתכן שהמכשיר במצב תקלה. נסה שוב או השתמש בקוד צימוד.");
        }
      } catch (e) {
        console.warn("status poll failed", e);
      }
    };
    const iv = setInterval(tick, 2500);
    tick();
    return () => { cancelled = true; clearInterval(iv); };
  }, [status, device?.id, mode, onConnected, qr]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="bg-stone-900 border-white/10 text-stone-100 sm:max-w-md" data-testid="ff-send-all-connect-dialog">
        <DialogHeader>
          <DialogTitle className="text-right flex items-center gap-2">
            <QrCode className="h-5 w-5 text-sky-400" />
            חיבור טלפון חדש
          </DialogTitle>
          <DialogDescription className="text-right text-stone-400 text-xs">
            סרוק QR עם וואטסאפ Web, או קבל קוד צימוד להזנה בטלפון.
          </DialogDescription>
        </DialogHeader>

        {status === "idle" && (
          <div className="space-y-3">
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setMode("qr")}
                className={`flex-1 rounded-md px-3 py-1.5 text-xs font-bold border transition ${mode === "qr" ? "bg-sky-500 border-sky-300 text-black" : "bg-white/5 border-white/15 hover:bg-white/10"}`}
                data-testid="ff-connect-mode-qr"
              >סריקת QR</button>
              <button
                type="button"
                onClick={() => setMode("pair")}
                className={`flex-1 rounded-md px-3 py-1.5 text-xs font-bold border transition ${mode === "pair" ? "bg-sky-500 border-sky-300 text-black" : "bg-white/5 border-white/15 hover:bg-white/10"}`}
                data-testid="ff-connect-mode-pair"
              >קוד צימוד</button>
            </div>
            <input
              type="tel"
              dir="ltr"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="972501234567"
              className="w-full bg-stone-800 border border-white/15 rounded-md px-3 py-2 text-sm outline-none focus:border-sky-400/60"
              data-testid="ff-connect-phone"
            />
            <Button
              onClick={startFlow}
              disabled={!phone.trim()}
              className="w-full bg-sky-500 hover:bg-sky-400 text-black font-extrabold"
              data-testid="ff-connect-start"
            >
              התחל חיבור
            </Button>
          </div>
        )}

        {status === "starting" && (
          <div className="text-center py-6 text-stone-400 text-sm">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
            יוצר סשן...
          </div>
        )}

        {status === "waiting" && mode === "qr" && (
          <div className="space-y-2 text-center">
            {qr ? (
              <img src={qr} alt="QR" className="mx-auto rounded-lg border border-white/20 bg-white" style={{ width: 280, height: 280 }} data-testid="ff-connect-qr-img" />
            ) : (
              <div className="text-stone-400 text-sm"><Loader2 className="h-5 w-5 animate-spin inline mr-1" /> מכין QR...</div>
            )}
            <div className="text-xs text-stone-400">פתח וואטסאפ → הגדרות → מכשירים מקושרים → קשר מכשיר → סרוק</div>
            <div className="text-xs text-sky-300">ממתין לחיבור...</div>
          </div>
        )}

        {status === "waiting" && mode === "pair" && (
          <div className="space-y-2 text-center">
            <div className="text-3xl font-mono tracking-widest text-emerald-300" data-testid="ff-connect-pair-code">{pairingCode || "..."}</div>
            <div className="text-xs text-stone-400">הזן את הקוד הזה בוואטסאפ → הגדרות → מכשירים מקושרים → קשר עם מספר טלפון</div>
            <div className="text-xs text-sky-300">ממתין לחיבור...</div>
          </div>
        )}

        {status === "connected" && (
          <div className="text-center py-6 text-emerald-300 text-sm font-bold">
            <CheckCircle2 className="h-8 w-8 mx-auto mb-2" />
            הטלפון חובר בהצלחה!
          </div>
        )}

        {status === "error" && (
          <div className="text-center py-4 text-red-300 text-sm">
            <XCircle className="h-6 w-6 mx-auto mb-2" />
            {msg || "שגיאה בחיבור"}
            <div className="pt-3">
              <Button onClick={() => setStatus("idle")} variant="outline" className="bg-transparent border-white/20 text-stone-200 hover:bg-white/10 h-8 text-xs">נסה שוב</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
